import { useEffect, useState } from "react";
import { useKid } from "@/context/KidContext";
import api from "@/lib/api";

export default function Passport() {
  const { active, kids, setActive } = useKid();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (active) api.get(`/progress/${active.id}`).then(({ data }) => setData(data));
  }, [active]);

  if (!active) return (
    <div className="p-10 text-center">
      <div className="text-4xl">🧒</div>
      <div className="mt-3 font-bold">Cadastre uma criança no seu perfil para ver o Passaporte.</div>
    </div>
  );

  const ranks = data?.ranks || [];
  const points = active.points || 0;
  const currentIndex = Math.max(0, ranks.findIndex((r) => r.key === active.rank?.key));
  const current = ranks[currentIndex];
  const next = ranks[currentIndex + 1];
  const progressFrom = current?.min_points ?? 0;
  const progressTo = next?.min_points ?? (current?.min_points ?? 0);
  const denom = Math.max(1, progressTo - progressFrom);
  const pct = next ? Math.min(100, Math.max(0, ((points - progressFrom) / denom) * 100)) : 100;
  const remaining = next ? Math.max(0, progressTo - points) : 0;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 pt-6 sm:pt-8 pb-16">
      <div className="flex items-center gap-2">
        <span className="text-3xl">🛂</span>
        <div>
          <h1 className="text-3xl font-extrabold text-[#243B53]">Passaporte da Fé</h1>
          <p className="text-[#243B53]/70 text-sm">Sua jornada de descobertas com Deus.</p>
        </div>
      </div>
      <div className="mt-3 h-1.5 rounded-full rainbow-strip" />

      {kids.length > 1 && (
        <div className="mt-4 flex gap-2 flex-wrap">
          {kids.map((k) => (
            <button key={k.id} data-testid={`passport-kid-${k.id}`} onClick={() => setActive(k.id)}
              className={`px-4 py-2 rounded-full font-bold text-sm ${k.id === active.id ? "bg-[#FFD54F] text-[#243B53]" : "bg-white border border-[#243B53]/10"}`}>
              {k.name}
            </button>
          ))}
        </div>
      )}

      {/* Stats */}
      <section className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <Stat emoji="⭐" label="Estrelas" value={active.stars || 0} />
        <Stat emoji="✨" label="Pontos" value={points} />
        <Stat emoji="🔥" label="Sequência" value={`${active.streak_days || 0} dias`} />
        <Stat emoji={active.rank?.emoji} label="Nível atual" value={active.rank?.label || "-"} />
      </section>

      {/* Progress to next */}
      <section className="mt-8 card-soft p-6" data-testid="passport-progress">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <div className="text-3xl">{current?.emoji}</div>
            <div>
              <div className="text-xs font-bold text-[#243B53]/60">Nível atual</div>
              <div className="font-extrabold text-[#243B53]">{current?.label}</div>
            </div>
          </div>
          {next ? (
            <div className="text-right">
              <div className="text-xs font-bold text-[#243B53]/60">Próximo</div>
              <div className="font-extrabold text-[#243B53]">{next.emoji} {next.label}</div>
            </div>
          ) : (
            <div className="text-right"><span className="chip chip-gold">🏆 Nível máximo!</span></div>
          )}
        </div>

        <div className="mt-4 flex items-center justify-between text-xs font-bold text-[#243B53]/70">
          <span>{points} / {next ? progressTo : progressFrom} pontos</span>
          <span>{next ? `Faltam ${remaining} pontos para ${next.label}` : "Parabéns, Mestre!"}</span>
        </div>
        <div className="mt-2 progress-track" role="progressbar" aria-valuenow={points} aria-valuemin={progressFrom} aria-valuemax={progressTo}>
          <div className="progress-fill" style={{ width: `${pct}%` }} />
        </div>
      </section>

      {/* Trail */}
      <section className="mt-10">
        <h2 className="text-xl font-extrabold text-[#243B53]">Trilha do Passaporte</h2>

        {/* Desktop trail */}
        <div className="mt-6 hidden sm:block relative">
          <div className="absolute left-[8%] right-[8%] top-1/2 -translate-y-1/2 h-[3px] trail-line" />
          <div className="relative grid grid-cols-5 gap-3">
            {ranks.map((r, i) => {
              const done = i <= currentIndex;
              const isCurrent = i === currentIndex;
              return (
                <div key={r.key} data-testid={`trail-node-${r.key}`} className="flex flex-col items-center">
                  <div className={`w-20 h-20 rounded-full flex items-center justify-center text-3xl ${
                    isCurrent ? "bg-white rainbow-ring animate-softpop"
                      : done ? "bg-[#FFD54F] border-4 border-white shadow"
                      : "bg-white border-2 border-dashed border-[#243B53]/20"
                  }`}>{r.emoji}</div>
                  <div className={`mt-3 font-extrabold text-center text-sm ${done ? "text-[#D9A441]" : "text-[#243B53]/50"}`}>
                    {r.label}
                  </div>
                  <div className="text-[10px] font-bold text-[#243B53]/50">{r.min_points} pts</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile trail (vertical) */}
        <div className="mt-6 sm:hidden relative pl-8">
          <div className="absolute left-4 top-2 bottom-2 w-[3px] trail-line-vert" />
          <ol className="space-y-4">
            {ranks.map((r, i) => {
              const done = i <= currentIndex;
              const isCurrent = i === currentIndex;
              return (
                <li key={r.key} className="relative flex items-center gap-3">
                  <div className={`-ml-8 w-12 h-12 rounded-full flex items-center justify-center text-xl ${
                    isCurrent ? "bg-white rainbow-ring"
                      : done ? "bg-[#FFD54F] border-2 border-white shadow"
                      : "bg-white border-2 border-dashed border-[#243B53]/20"
                  }`}>{r.emoji}</div>
                  <div>
                    <div className={`font-extrabold ${done ? "text-[#D9A441]" : "text-[#243B53]/60"}`}>{r.label}</div>
                    <div className="text-xs text-[#243B53]/60">{r.min_points} pts</div>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      {/* Achievements */}
      <section className="mt-12">
        <h2 className="text-xl font-extrabold text-[#243B53]">Conquistas</h2>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {(data?.achievements || []).map((a) => (
            <div key={a.key} data-testid={`achievement-${a.key}`}
              className={`card-soft p-5 flex items-center gap-4 ${a.unlocked ? "achievement-unlocked" : "achievement-locked"}`}>
              <div className="text-4xl">{a.unlocked ? a.emoji : "🔒"}</div>
              <div className="flex-1">
                <div className={`font-extrabold ${a.unlocked ? "rainbow-text" : "text-[#243B53]"}`}>{a.label}</div>
                <div className="text-sm text-[#243B53]/70">{a.desc}</div>
                <div className="mt-1 text-xs font-bold" style={{ color: a.unlocked ? "#7BC67B" : "#243B53" }}>
                  {a.unlocked ? "🎉 Conquistado" : "A conquistar"}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Recent activities */}
      <section className="mt-10">
        <h2 className="text-xl font-extrabold text-[#243B53]">Atividades recentes</h2>
        {(data?.recent?.length ?? 0) === 0 ? (
          <div className="mt-4 card-soft p-6 text-center text-[#243B53]/60">Nada por aqui ainda.</div>
        ) : (
          <ul className="mt-4 card-soft divide-y divide-[#243B53]/10">
            {data.recent.map((p) => (
              <li key={p.id} className="p-4 flex items-center justify-between">
                <div><span className="chip chip-sun capitalize mr-2">{p.kind}</span>{p.date}</div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function Stat({ emoji, label, value }) {
  return (
    <div className="card-soft p-4 text-center">
      <div className="text-3xl">{emoji}</div>
      <div className="mt-1 text-[#243B53]/60 text-xs font-bold">{label}</div>
      <div className="font-extrabold text-lg sm:text-xl text-[#243B53] truncate">{value}</div>
    </div>
  );
}
